import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import * as estoqueApi from '../../api/estoque';
import { useAuth } from '../../contexts/AuthContext';
import { useEmpresaSelecionada } from '../../contexts/EmpresaContext';
import { useExigeEmpresaEspecifica } from '../../hooks/useExigeEmpresaEspecifica';

const ITENS = [
  { to: '/dashboard', icon: 'bi-speedometer2', label: 'Dashboard' },
  { to: '/pedidos', icon: 'bi-cart-check', label: 'Saídas (Pedidos)' },
  { to: '/estoque', icon: 'bi-boxes', label: 'Estoque' },
  { to: '/lotes', icon: 'bi-truck', label: 'Entradas (Lotes)' },
  { to: '/ajustes', icon: 'bi-exclamation-triangle', label: 'Ajustes / Perdas' },
  { to: '/relatorios', icon: 'bi-file-earmark-spreadsheet', label: 'Relatórios' },
];

const CADASTROS = [
  { to: '/produtos', icon: 'bi-basket', label: 'Produtos' },
  { to: '/fornecedores', icon: 'bi-truck-flatbed', label: 'Fornecedores' },
  { to: '/clientes', icon: 'bi-people', label: 'Clientes' },
  { to: '/qualidades', icon: 'bi-award', label: 'Classificações de Qualidade' },
  { to: '/unidades', icon: 'bi-rulers', label: 'Unidades de Medida' },
];

function Item({ to, icon, label, badge, onNavigate }) {
  return (
    <NavLink to={to} className="sf-nav-link" onClick={onNavigate} title={label}>
      <i className={`bi ${icon}`} />
      <span>{label}</span>
      {badge > 0 && (
        <span className="sf-nav-badge" title="Produtos abaixo do estoque mínimo">
          {badge}
        </span>
      )}
    </NavLink>
  );
}

export default function Sidebar({ mobileOpen, recolhido, onToggleRecolhido, onNavigate }) {
  const { user } = useAuth();
  const { idEmpresa } = useEmpresaSelecionada();
  const { bloqueado } = useExigeEmpresaEspecifica();
  const [estoqueBaixo, setEstoqueBaixo] = useState(0);

  useEffect(() => {
    // super_admin em modo Global (sem empresa específica selecionada) não tem como calcular
    // um total de estoque baixo agregado — mesma regra da própria tela de Estoque.
    if (bloqueado) {
      setEstoqueBaixo(0);
      return;
    }
    const carregar = () => {
      estoqueApi
        .getEstoqueBaixoCount()
        .then((r) => setEstoqueBaixo(r.data?.total || 0))
        .catch(() => setEstoqueBaixo(0));
    };
    carregar();
    // reforça o badge periodicamente — o operador costuma deixar a aba aberta o turno todo,
    // e o saldo muda com pedidos/ajustes de outros usuários.
    const intervalo = setInterval(carregar, 3 * 60 * 1000);
    return () => clearInterval(intervalo);
  }, [bloqueado, idEmpresa]);

  return (
    <>
      {mobileOpen && <div className="sf-overlay d-lg-none" onClick={onNavigate} />}
      <aside className={`sf-sidebar ${mobileOpen ? 'sf-sidebar-open' : ''}`}>
        <div className="sf-sidebar-header">
          <svg width="32" height="32" viewBox="0 0 64 64" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
            <rect width="64" height="64" rx="17" fill="#2f5233" />
            <rect x="15" y="17" width="34" height="9" rx="4.5" fill="#f4e389" />
            <rect x="15" y="29.5" width="24" height="9" rx="4.5" fill="#ffffff" />
            <rect x="15" y="42" width="34" height="9" rx="4.5" fill="#ffffff" opacity="0.55" />
          </svg>
          {!recolhido && (
            <>
              <span className="sf-sidebar-brand">StoreFlow</span>
              <button
                type="button"
                className="sf-icon-btn d-none d-lg-inline-flex ms-auto"
                onClick={onToggleRecolhido}
                title="Recolher menu"
                aria-label="Recolher menu"
              >
                <i className="bi bi-chevron-double-left" />
              </button>
            </>
          )}
        </div>

        <nav className="sf-sidebar-body">
          {ITENS.map((item) => (
            <Item
              key={item.to}
              {...item}
              badge={item.to === '/estoque' ? estoqueBaixo : undefined}
              onNavigate={onNavigate}
            />
          ))}

          <div className="sf-nav-section-title"><span>Cadastros</span></div>
          {CADASTROS.map((item) => (
            <Item key={item.to} {...item} onNavigate={onNavigate} />
          ))}

          {(user?.perfil === 'admin' || user?.perfil === 'super_admin') && (
            <>
              <div className="sf-nav-section-title"><span>Administração</span></div>
              <Item to="/usuarios" icon="bi-person-badge" label="Usuários" onNavigate={onNavigate} />
              {user?.perfil === 'super_admin' && (
                <Item to="/empresas" icon="bi-building" label="Empresas" onNavigate={onNavigate} />
              )}
            </>
          )}

          {recolhido && (
            <button
              type="button"
              className="sf-icon-btn d-none d-lg-inline-flex mt-3"
              onClick={onToggleRecolhido}
              title="Expandir menu"
              aria-label="Expandir menu"
            >
              <i className="bi bi-chevron-double-right" />
            </button>
          )}
        </nav>
      </aside>
    </>
  );
}
